import { useEffect, useState } from 'react';
import { Cookie, X } from 'lucide-react';
import {
  getConsent, setConsent, CONSENT_ACCEPTED, CONSENT_REJECTED,
} from '@/lib/consent';

/**
 * Minimal GDPR cookie-consent banner.
 * - First-visit only (hidden once a decision is stored).
 * - Decision persists for 12 months (see lib/consent.js).
 * - No third-party library. ~1.5 KB min-gzipped.
 */
export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!getConsent()) setVisible(true);
  }, []);

  if (!visible) return null;

  const decide = (decision) => {
    setConsent(decision);
    setVisible(false);
    // Full reload ensures rejected scripts cannot be loaded later in the same
    // session via stale module state, and accepted scripts pick up IDs on boot.
    if (decision === CONSENT_ACCEPTED) window.location.reload();
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
      className="fixed inset-x-0 bottom-0 z-40 p-3 sm:p-4 pointer-events-none"
    >
      <div className="pointer-events-auto mx-auto max-w-4xl rounded-xl border border-border bg-background/95 backdrop-blur-md shadow-lg p-4 flex flex-col sm:flex-row items-start gap-4">
        <div className="flex items-start gap-3 flex-1">
          <div className="w-9 h-9 rounded-full bg-amber-500/10 flex items-center justify-center shrink-0">
            <Cookie className="w-4 h-4 text-amber-500" />
          </div>
          <div className="text-sm">
            <p className="font-semibold mb-1">We use cookies</p>
            <p className="text-muted-foreground text-xs leading-relaxed">
              We use cookies to enhance your experience. By accepting, you allow us
              to track usage and improve the platform.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 self-stretch sm:self-center w-full sm:w-auto">
          <button
            onClick={() => decide(CONSENT_REJECTED)}
            data-testid="cookie-reject-btn"
            className="flex-1 sm:flex-none px-4 py-2 text-xs font-medium rounded-md border border-border hover:bg-muted transition-colors"
          >
            Reject All
          </button>
          <button
            onClick={() => decide(CONSENT_ACCEPTED)}
            data-testid="cookie-accept-btn"
            className="flex-1 sm:flex-none px-4 py-2 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Accept All
          </button>
          <button
            onClick={() => decide(CONSENT_REJECTED)}
            aria-label="Close and reject"
            data-testid="cookie-close-btn"
            className="p-2 rounded-md hover:bg-muted text-muted-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
